"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { keepPreviousData, type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiWaveOutcomesPage } from "@/generated/models/ApiWaveOutcomesPage";
import { commonApiFetch } from "@/services/api/common-api";
export type WaveOutcomesSortDirection = "ASC" | "DESC";
export interface UseWaveOutcomesQueryParams {
  readonly waveId?: string | null | undefined;
  readonly pageSize?: number | undefined;
  readonly sortDirection?: WaveOutcomesSortDirection | undefined;
  readonly enabled?: boolean | undefined;
}
type WaveOutcomesInfiniteData = InfiniteData<ApiWaveOutcomesPage>;
export type UseWaveOutcomesQueryResult = UseInfiniteQueryResult<WaveOutcomesInfiniteData, Error> & {
  readonly outcomes: ApiWaveOutcomesPage["data"];
  readonly errorMessage?: string | undefined;
  readonly isEnabled: boolean;
};
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_SORT_DIRECTION: WaveOutcomesSortDirection = "DESC";
const DEFAULT_STALE_TIME = 30_000;
export function useWaveOutcomesQuery(t0) {
  const $ = _c(24);
  const {
    waveId,
    pageSize: t1,
    sortDirection: t2,
    enabled: t3
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_PAGE_SIZE : t1;
  const sortDirection = t2 === undefined ? DEFAULT_SORT_DIRECTION : t2;
  const enabled = t3 === undefined ? true : t3;
  let t4;
  if ($[0] !== waveId) {
    t4 = waveId?.trim() ?? "";
    $[0] = waveId;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const normalizedWaveId = t4;
  const hasWaveId = Boolean(normalizedWaveId);
  const isEnabled = hasWaveId && enabled;
  let t5;
  if ($[2] !== pageSize) {
    t5 = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : DEFAULT_PAGE_SIZE;
    $[2] = pageSize;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const normalizedPageSize = t5;
  const normalizedSortDirection = sortDirection === "ASC" ? "ASC" : "DESC";
  let t6;
  if ($[4] !== normalizedPageSize || $[5] !== normalizedSortDirection || $[6] !== normalizedWaveId) {
    t6 = [QueryKey.WAVE_OUTCOMES, normalizedWaveId, normalizedPageSize, normalizedSortDirection];
    $[4] = normalizedPageSize;
    $[5] = normalizedSortDirection;
    $[6] = normalizedWaveId;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const queryKey = t6;
  let t7;
  if ($[8] !== normalizedPageSize || $[9] !== normalizedSortDirection || $[10] !== normalizedWaveId) {
    t7 = async t8 => {
      const {
        pageParam
      } = t8;
      const currentPage = pageParam ?? DEFAULT_PAGE;
      const params = {
        page: currentPage.toString(),
        page_size: normalizedPageSize.toString(),
        sort_direction: normalizedSortDirection
      };
      return commonApiFetch({
        endpoint: `waves/${normalizedWaveId}/outcomes`,
        params
      });
    };
    $[8] = normalizedPageSize;
    $[9] = normalizedSortDirection;
    $[10] = normalizedWaveId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getDefaultQueryRetry();
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isEnabled || $[14] !== queryKey || $[15] !== t7) {
    t9 = {
      queryKey,
      queryFn: t7,
      initialPageParam: DEFAULT_PAGE,
      getNextPageParam: _temp,
      enabled: isEnabled,
      staleTime: DEFAULT_STALE_TIME,
      placeholderData: keepPreviousData,
      ...t8
    };
    $[13] = isEnabled;
    $[14] = queryKey;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const query = useInfiniteQuery(t9);
  let t10;
  if ($[17] !== query.data?.pages) {
    t10 = query.data?.pages.flatMap(_temp2) ?? [];
    $[17] = query.data?.pages;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const outcomes = t10;
  const errorMessage = query.error instanceof Error ? query.error.message : undefined;
  let t11;
  if ($[19] !== errorMessage || $[20] !== isEnabled || $[21] !== outcomes || $[22] !== query) {
    t11 = {
      ...query,
      outcomes,
      errorMessage,
      isEnabled
    };
    $[19] = errorMessage;
    $[20] = isEnabled;
    $[21] = outcomes;
    $[22] = query;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
function _temp2(pageData) {
  return pageData.data;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}